/**
 * useStructuredData Composable
 * 
 * Applying Performance Optimization Pattern from PATTERNS_FROM_BASKET_BASE.md
 * 
 * Optimizes SEO structured data injection and cleanup for better performance
 * and memory management. Eliminates memory leaks from direct DOM manipulation.
 * 
 * Following ADR-045: Vue Performance Optimizations Phase 2
 * - Centralized structured data management
 * - Automatic cleanup on unmount
 * - Memory leak prevention
 * - SEO consistency across components
 */

import { onMounted, onUnmounted } from 'vue'

/**
 * Composable for managing structured data injection and cleanup
 * 
 * @param {Object} schemaData - The structured data object to inject
 * @returns {Object} Methods for manual control if needed
 */
export function useStructuredData(schemaData = null) {
  // Track injected elements for cleanup
  const injectedElements = new Set()

  /**
   * Inject structured data script into document head
   * 
   * @param {Object} data - Schema.org structured data object
   * @returns {HTMLScriptElement} The created script element
   */
  const injectStructuredData = (data) => {
    if (!data) return null

    // Create script element for structured data
    const script = document.createElement('script')
    script.type = 'application/ld+json'
    script.textContent = JSON.stringify(data)
    script.dataset.structuredData = 'true'
    
    // Inject into head
    document.head.appendChild(script)
    
    // Track for cleanup
    injectedElements.add(script)
    
    return script
  }

  /**
   * Update page meta tags for SEO optimization
   * 
   * @param {Object} metaData - Object with title, description, etc.
   */
  const updateMetaTags = (metaData) => {
    if (metaData.title) {
      document.title = metaData.title
    }

    if (metaData.description) {
      let metaDescription = document.querySelector('meta[name="description"]')
      
      if (metaDescription) {
        metaDescription.setAttribute('content', metaData.description)
      } else {
        metaDescription = document.createElement('meta')
        metaDescription.name = 'description'
        metaDescription.content = metaData.description
        document.head.appendChild(metaDescription)
        injectedElements.add(metaDescription)
      }
    }

    // Handle additional meta tags
    if (metaData.keywords) {
      let metaKeywords = document.querySelector('meta[name="keywords"]')
      
      if (metaKeywords) {
        metaKeywords.setAttribute('content', metaData.keywords)
      } else {
        metaKeywords = document.createElement('meta')
        metaKeywords.name = 'keywords'
        metaKeywords.content = metaData.keywords
        document.head.appendChild(metaKeywords)
        injectedElements.add(metaKeywords)
      }
    }
  }

  /**
   * Clean up all injected scripts and meta tags
   */
  const cleanupScripts = () => {
    injectedElements.forEach(element => {
      if (element.parentNode) {
        element.parentNode.removeChild(element)
      }
    })
    injectedElements.clear()
  }

  /**
   * Setup structured data for Olvidadme legal services
   * 
   * @param {Object} customData - Optional custom data to merge
   * @returns {Object} The complete structured data object
   */
  const setupLegalServiceSchema = (customData = {}) => {
    const baseSchema = {
      "@context": "https://schema.org",
      "@type": "LegalService",
      "name": "Olvidadme",
      "description": "Servicios especializados en derecho digital, derecho al olvido y gestión GDPR",
      "url": window.location.origin,
      "telephone": "+34-900-123-456",
      "email": "info@olvidadme.com",
      "address": {
        "@type": "PostalAddress",
        "addressCountry": "España"
      },
      "serviceType": [
        "Derecho al Olvido",
        "Derecho al Olvido Post-Mortem",
        "Testamento Digital"
      ],
      "areaServed": {
        "@type": "Country",
        "name": "España"
      },
      "legalName": "Olvidadme - Servicios Jurídicos Digitales",
      "alternateName": "Olvidadme Legal Tech",
      "sameAs": [
        "https://linkedin.com/company/olvidadme",
        "https://twitter.com/olvidadme"
      ]
    }

    // Merge with custom data
    return { ...baseSchema, ...customData }
  }

  // Auto-inject if schema data provided
  onMounted(() => {
    if (schemaData) {
      injectStructuredData(schemaData)
    }
  })

  // Cleanup on unmount
  onUnmounted(() => {
    cleanupScripts()
  })

  return {
    injectStructuredData,
    updateMetaTags,
    cleanupScripts,
    setupLegalServiceSchema
  }
}